export type LectioReaderSection = 'read' | 'character-index' | 'compare'
export type LectioEvidenceKind = 'name' | 'coref' | 'speaker' | 'focalization' | 'characterization'
export type LectioProjection = 'full-text' | 'chain' | 'speaker' | 'focalization' | 'characterization' | 'analytic'

export interface LectioCharacterIdentity {
  id: string
  label: string
}

export interface LectioEvidenceRecord {
  id: string
  characterId: string
  referentCharacterIds?: readonly string[]
  witnessId: string
  rowId: string
  start: number
  end: number
  kind: LectioEvidenceKind
  text: string
  sourceKind?: 'occurrence' | 'layer'
  standing?: 'admitted' | 'received' | 'subjudice' | 'relegated'
  subtype?: string
  voice?: string
  /** Several row-sized evidence records may belong to one logical speech turn. */
  groupId?: string
}

export interface LectioEvidenceCounts {
  chain: number
  speaker: number
  focalization: number
  characterization: number
}

export interface LectioRosterEntry extends LectioCharacterIdentity {
  counts: LectioEvidenceCounts
  targetEvidenceId: string
}

export interface LectioProjectionPiece {
  type: 'span' | 'ellipsis'
  evidenceId?: string
  evidenceIds?: string[]
  characterId?: string
  rowId?: string
  start?: number
  end?: number
  kind?: LectioEvidenceKind
  kinds?: LectioEvidenceKind[]
  text?: string
}

export interface LectioReaderState {
  section: LectioReaderSection
  sourceWitnessId: string
  selectedRowId: string
  selectedCharacterId: string
  focusedEvidenceId: string | null
  actionMenuCharacterId: string
  projection: LectioProjection
  assistanceOpen: boolean
}

interface LectioEvidenceInput {
  evidence: readonly LectioEvidenceRecord[]
  rowOrder: readonly string[]
  witnessId: string
}

interface LectioRosterInput extends LectioEvidenceInput {
  characters: readonly LectioCharacterIdentity[]
}

interface LectioProjectionInput extends LectioEvidenceInput {
  characterId: string
  kinds: readonly LectioEvidenceKind[]
}

const isChainEvidence = (kind: LectioEvidenceKind): boolean => kind === 'name' || kind === 'coref'

export const getLectioEvidenceCharacterIds = (
  item: Pick<LectioEvidenceRecord, 'characterId' | 'referentCharacterIds'>,
): string[] => Array.from(new Set([
  item.characterId,
  ...(Array.isArray(item.referentCharacterIds) ? item.referentCharacterIds : []),
].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)))

export const lectioEvidenceRefersTo = (
  item: Pick<LectioEvidenceRecord, 'characterId' | 'referentCharacterIds'>,
  characterId: string,
): boolean => getLectioEvidenceCharacterIds(item).includes(characterId)

const buildRowOrder = (rowOrder: readonly string[]): Map<string, number> => (
  new Map(rowOrder.map((rowId, index) => [rowId, index]))
)

const compareEvidence = (
  left: LectioEvidenceRecord,
  right: LectioEvidenceRecord,
  rowOrderById: ReadonlyMap<string, number>,
): number => (
  (rowOrderById.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
    (rowOrderById.get(right.rowId) ?? Number.MAX_SAFE_INTEGER) ||
  left.start - right.start ||
  left.end - right.end ||
  left.id.localeCompare(right.id)
)

const getScopedEvidence = ({
  evidence,
  rowOrder,
  witnessId,
}: LectioEvidenceInput): LectioEvidenceRecord[] => {
  const rowOrderById = buildRowOrder(rowOrder)
  const seenEvidenceIds = new Set<string>()
  return evidence
    .filter(item => {
      const isPlaced = item.witnessId === witnessId &&
      rowOrderById.has(item.rowId) &&
      Number.isFinite(item.start) &&
      Number.isFinite(item.end) &&
      item.start >= 0 &&
      item.end > item.start &&
      !seenEvidenceIds.has(item.id)
      if (isPlaced) {
        seenEvidenceIds.add(item.id)
      }
      return isPlaced
    })
    .sort((left, right) => compareEvidence(left, right, rowOrderById))
}

export const buildLectioRoster = ({
  characters,
  evidence,
  rowOrder,
  witnessId,
}: LectioRosterInput): LectioRosterEntry[] => {
  const characterById = new Map<string, LectioCharacterIdentity>()
  const characterOrderById = new Map<string, number>()
  characters.forEach((character, index) => {
    if (!characterById.has(character.id)) {
      characterById.set(character.id, character)
      characterOrderById.set(character.id, index)
    }
  })

  const rowOrderById = buildRowOrder(rowOrder)
  const scopedEvidence = getScopedEvidence({ evidence, rowOrder, witnessId })
  const evidenceByCharacterId = new Map<string, LectioEvidenceRecord[]>()
  scopedEvidence.forEach(item => {
    getLectioEvidenceCharacterIds(item).forEach(characterId => {
      if (!characterById.has(characterId)) {
        return
      }
      const bucket = evidenceByCharacterId.get(characterId) || []
      bucket.push(item)
      evidenceByCharacterId.set(characterId, bucket)
    })
  })
  const firstEvidenceByCharacterId = new Map(Array.from(evidenceByCharacterId.entries()).map(
    ([characterId, characterEvidence]) => [characterId, characterEvidence[0]],
  ))

  return Array.from(characterById.values())
    .map(character => {
      const characterEvidence = evidenceByCharacterId.get(character.id) || []
      const chainEvidence = characterEvidence.filter(item => isChainEvidence(item.kind))
      const speakerTurnIds = new Set(characterEvidence
        .filter(item => item.kind === 'speaker')
        .map(item => item.groupId || item.id))
      const target = chainEvidence[0] || characterEvidence[0]
      return {
        ...character,
        counts: {
          chain: chainEvidence.length,
          speaker: speakerTurnIds.size,
          focalization: characterEvidence.filter(item => item.kind === 'focalization').length,
          characterization: characterEvidence.filter(item => item.kind === 'characterization').length,
        },
        targetEvidenceId: target?.id || '',
      }
    })
    .sort((left, right) => {
      const leftFirstEvidence = firstEvidenceByCharacterId.get(left.id)
      const rightFirstEvidence = firstEvidenceByCharacterId.get(right.id)
      if (leftFirstEvidence && rightFirstEvidence) {
        return compareEvidence(leftFirstEvidence, rightFirstEvidence, rowOrderById) || left.id.localeCompare(right.id)
      }
      if (leftFirstEvidence) {
        return -1
      }
      if (rightFirstEvidence) {
        return 1
      }
      return (characterOrderById.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
        (characterOrderById.get(right.id) ?? Number.MAX_SAFE_INTEGER)
    })
}

export const projectLectioEvidence = ({
  characterId,
  evidence,
  kinds,
  rowOrder,
  witnessId,
}: LectioProjectionInput): LectioProjectionPiece[] => {
  const kindSet = new Set(kinds)
  const selectedEvidence = getScopedEvidence({ evidence, rowOrder, witnessId })
    .filter(item => lectioEvidenceRefersTo(item, characterId) && kindSet.has(item.kind))
  const pieces: LectioProjectionPiece[] = []
  const mergedSpans: LectioProjectionPiece[] = []

  selectedEvidence.forEach(item => {
    const previous = mergedSpans[mergedSpans.length - 1]
    if (
      previous?.type === 'span' &&
      previous.rowId === item.rowId &&
      previous.end !== undefined &&
      item.start <= previous.end
    ) {
      const previousEnd = previous.end
      const appendedText = item.end > previousEnd
        ? item.text.slice(Math.max(0, previousEnd - item.start))
        : ''
      previous.end = Math.max(previousEnd, item.end)
      previous.text = `${previous.text || ''}${appendedText}`
      previous.evidenceIds = [...(previous.evidenceIds || (previous.evidenceId ? [previous.evidenceId] : [])), item.id]
      previous.kinds = Array.from(new Set([...(previous.kinds || (previous.kind ? [previous.kind] : [])), item.kind]))
      return
    }
    mergedSpans.push({
      type: 'span',
      evidenceId: item.id,
      evidenceIds: [item.id],
      characterId: item.characterId,
      rowId: item.rowId,
      start: item.start,
      end: item.end,
      kind: item.kind,
      kinds: [item.kind],
      text: item.text,
    })
  })

  mergedSpans.forEach((item, index) => {
    const previous = mergedSpans[index - 1]
    if (previous && (previous.rowId !== item.rowId || (previous.end ?? -1) < (item.start ?? -1))) {
      pieces.push({ type: 'ellipsis' })
    }
    pieces.push(item)
  })

  return pieces
}

export const buildTextualBeingCards = ({
  characters,
  evidence,
  rowOrder,
  witnessId,
}: LectioRosterInput): Array<LectioCharacterIdentity & { pieces: LectioProjectionPiece[] }> => (
  buildLectioRoster({ characters, evidence, rowOrder, witnessId }).map(character => ({
    id: character.id,
    label: character.label,
    pieces: projectLectioEvidence({
      characterId: character.id,
      evidence,
      kinds: ['focalization', 'characterization'],
      rowOrder,
      witnessId,
    }),
  }))
)

export const transitionLectioReaderState = (
  state: LectioReaderState,
  section: LectioReaderSection,
): LectioReaderState => ({
  ...state,
  section,
  selectedCharacterId: section === 'character-index' ? state.selectedCharacterId : '',
  focusedEvidenceId: section === 'character-index' ? state.focusedEvidenceId : null,
  actionMenuCharacterId: section === 'character-index' ? state.actionMenuCharacterId : '',
  projection: section === 'character-index' ? state.projection : 'full-text',
  assistanceOpen: section === 'character-index' ? state.assistanceOpen : false,
})
